/**
 * Instant loading placeholders for embedded hosts.
 *
 * An embedded `<div data-calendoula-today>` / `<div data-calendoula-shop>` /
 * `<div data-calendoula-form>` / `<div data-calendoula-memberships>` sits blank while the remote widget
 * config and then the on-demand bundle (quick-book.js / catalog.js) load — the
 * bundles' own loading states only render once their script executes. These
 * helpers paint a simple state immediately — the Calendoula logo breathing
 * over one line of text — which the real mount overwrites.
 */

const STYLE_ID = 'pp-embed-ph-styles';

const CSS = `
.pp-ph { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; border: 1px solid #eadfd6; border-radius: 14px; background: #fff; padding: 36px 20px; }
.pp-ph-logo { width: 44px; height: 44px; animation: pp-ph-breathe 1.8s ease-in-out infinite; }
.pp-ph-line { display: flex; align-items: center; gap: 8px; font-size: 13.5px; color: #8a7f76; margin: 0; }
.pp-ph-spin { display: inline-block; width: 14px; height: 14px; border: 2px solid #e8ddd3; border-top-color: #b8623e; border-radius: 50%; animation: pp-ph-spin 1.4s linear infinite; flex: none; }
.pp-ph-banner { flex-direction: row; justify-content: flex-start; padding: 10px 14px; }
.pp-ph-banner .pp-ph-logo { width: 20px; height: 20px; }
@keyframes pp-ph-breathe { 0%, 100% { opacity: 0.55; transform: scale(0.94); } 50% { opacity: 1; transform: scale(1); } }
@keyframes pp-ph-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .pp-ph-logo, .pp-ph-spin { animation: none; } }
`;

export type PlaceholderKind = 'today' | 'banner' | 'shop' | 'form' | 'memberships' | 'menu';

const TEXT: Record<PlaceholderKind, string> = {
  today: 'Checking today’s openings…',
  banner: 'Checking today’s openings…',
  shop: 'Loading the shop…',
  form: 'Loading form…',
  memberships: 'Loading memberships…',
  menu: 'Loading the menu…',
};

function ensureStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = CSS;
  document.head.appendChild(s);
}

/** The Calendoula mark served next to the widget bundle (also used by "Powered by"). */
export function embedLogoUrl(apiBase: string): string {
  try { return `${new URL(apiBase).origin}/static/calendoula-logo-sm.png`; } catch { return ''; }
}

function markup(kind: PlaceholderKind, logoUrl: string): string {
  // No resolvable logo (relative/mock apiBase): a small spinner keeps the
  // state alive instead of a broken image.
  const mark = logoUrl
    ? `<img class="pp-ph-logo" src="${logoUrl}" alt="" width="44" height="44">`
    : `<span class="pp-ph-spin"></span>`;
  const banner = kind === 'banner' ? ' pp-ph-banner' : '';
  return `<div class="pp-ph${banner}" aria-busy="true">${mark}<p class="pp-ph-line">${TEXT[kind]}</p></div>`;
}

/**
 * Paint a placeholder into an embed host. A host that already has content
 * (a fallback the site owner put there, an earlier placeholder, or a live
 * mount) is left alone — the real mount overwrites innerHTML anyway.
 */
export function paintEmbedPlaceholder(el: HTMLElement, kind: PlaceholderKind, logoUrl = ''): void {
  if (el.childElementCount > 0) return;
  ensureStyles();
  el.innerHTML = markup(kind, logoUrl);
}

/** Remove a placeholder we painted (bundle failed to load) — never touches other content. */
export function clearEmbedPlaceholder(el: HTMLElement): void {
  if (el.querySelector(':scope > .pp-ph')) el.innerHTML = '';
}
